/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, ReactNode, useState } from 'react';
import { Badge } from '@crisellevaldez/speculo-ui';
import { ArrowRight } from 'lucide-react';
import { getWebActivityEventTypeStyle, getEmailOutcomeStyle, formatEmailOutcome } from '../../utils/badgeStyles';
import { OutcomeBadge } from './OutcomeBadge';
import { formatCallDuration } from '../../utils/duration';
import { previewEmail } from '../../utils/email/previewEmail';
import { linkify } from '../../utils/linkify';

const SUMMARY_LIMIT = 150;

export interface ActivityContentProps {
  /** Activity event type — call, email, or web types (property_view, search, etc.) */
  eventType: string;
  /** Duration in seconds (calls) */
  durationSeconds?: number;
  /** Call outcome (Voicemail, Follow Up, etc.) */
  outcome?: string;
  /** Email body HTML for preview */
  emailBodyHtml?: string;
  /** Extra email metadata from parsed-JSON metadata (subject, from, to,
   *  template). Passed through to `previewEmail` so the modal can
   *  headline the real subject (e.g. "5 New Properties in Austin")
   *  instead of falling back to a body snippet. */
  emailMeta?: {
    subject?: string;
    fromAddress?: string;
    toAddress?: string;
    templateName?: string;
    sentAt?: string;
    contactName?: string;
    contactEmail?: string;
  };
  /** Web event badge info */
  webBadge?: { label: string; className: string };
  /** Email lifecycle badge info (delivered / opened / clicked / bounced / unsubscribed) */
  emailBadge?: { label: string; className: string };
  /** Description — string or ReactNode (clickable links for web) */
  description?: ReactNode;
  /** Renders a Phone icon button on call rows that opens a call-details
   *  modal — independent of the row click which still opens the contact
   *  edit modal. Omit to hide the button. */
  onViewCall?: () => void;
  /** Same shape as onViewCall but for SMS rows — renders a MessageSquare
   *  icon button that opens the SMS thread modal. */
  onViewSms?: () => void;
}

/** Inline badges row — duration, outcome, email preview, web event badge */
export const ActivityInlineBadges: FC<ActivityContentProps> = ({
  eventType,
  durationSeconds,
  outcome,
  emailBodyHtml,
  emailMeta,
  webBadge,
  emailBadge,
  onViewCall,
  onViewSms,
}) => {
  const type = eventType?.toLowerCase() ?? '';
  const isCall = type === 'call' || type.includes('call');
  const isSms = type === 'sms';

  return (
    <>
      {isCall && typeof durationSeconds === 'number' && durationSeconds > 0 && (
        <span className="text-xs text-muted-foreground">
          {formatCallDuration(durationSeconds)}
        </span>
      )}
      {isCall && outcome && (
        <OutcomeBadge outcome={outcome} className="px-1.5 py-0 shrink-0" />
      )}
      {isCall && onViewCall && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onViewCall();
          }}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-600 dark:text-primaryfortext hover:underline shrink-0 opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity"
        >
          View call
          <ArrowRight className="w-3 h-3" />
        </button>
      )}
      {isSms && onViewSms && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onViewSms();
          }}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-600 dark:text-primaryfortext hover:underline shrink-0 opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity"
        >
          Open thread
          <ArrowRight className="w-3 h-3" />
        </button>
      )}
      {webBadge && (
        <Badge className={`${webBadge.className} px-1.5 py-0 shrink-0`}>
          {webBadge.label}
        </Badge>
      )}
      {emailBadge && (
        <Badge className={`${emailBadge.className} px-1.5 py-0 shrink-0`}>
          {emailBadge.label}
        </Badge>
      )}
      {emailBodyHtml && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            previewEmail({ bodyHtml: emailBodyHtml, ...(emailMeta || {}) });
          }}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-600 dark:text-primaryfortext hover:underline shrink-0 opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity"
        >
          Preview email
          <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </>
  );
};

/**
 * Helper to build emailBadge from event type + outcome.
 * Returns undefined for non-email events or when the outcome doesn't map to a known lifecycle.
 */
export function buildEmailBadge(
  eventType: string,
  outcome?: string | null,
): ActivityContentProps['emailBadge'] {
  const evtType = eventType?.toLowerCase() ?? '';
  if (evtType !== 'email') return undefined;
  const style = getEmailOutcomeStyle(outcome);
  if (!style || !outcome) return undefined;
  return {
    label: formatEmailOutcome(outcome),
    className: `${style.background} ${style.text}`.trim(),
  };
}

/** Description block — handles both string (with show more/less) and ReactNode (clickable links) */
export const ActivityDescriptionBlock: FC<{ description?: ReactNode; maxLength?: number }> = ({
  description,
  maxLength = SUMMARY_LIMIT,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (!description) return null;

  if (typeof description !== 'string') {
    return (
      <div className="text-muted-foreground mt-1 text-xs xl:text-sm max-w-[900px]">
        {description}
      </div>
    );
  }

  const isLong = description.length > maxLength;
  const display = isLong && !expanded
    ? description.slice(0, maxLength).trimEnd() + '…'
    : description;

  if (!display) return null;

  return (
    <p className="text-muted-foreground mt-1 text-xs xl:text-sm max-w-[900px]">
      {linkify(display, { className: 'text-primary hover:text-primary/80' })}
      {isLong && (
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          className="ml-1 text-foreground/60 underline underline-offset-2 text-[10px] font-medium uppercase tracking-wide hover:text-foreground transition-colors"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </p>
  );
};

/** Helper to build webBadge from event type + metadata */
export function buildWebBadge(
  eventType: string,
  eventCategory?: string,
  pageUrl?: string,
): ActivityContentProps['webBadge'] {
  const evtType = eventType?.toLowerCase() ?? '';
  const webTypes = ['property_view', 'property_saved', 'lead_capture'];
  if (!webTypes.includes(evtType)) return undefined;

  if (evtType === 'property_view') {
    const isSeller = (eventCategory || '').includes('Valuation') || (pageUrl || '').includes('valuation');
    return {
      label: isSeller ? 'Home Valuation - Seller' : 'Property View - Buyer',
      className: isSeller ? 'web-evt-property-seller' : 'web-evt-property-buyer',
    };
  }
  if (evtType === 'property_saved') {
    return { label: 'Saved Property', className: 'web-evt-property-buyer' };
  }
  const s = getWebActivityEventTypeStyle(evtType, eventCategory || '');
  if (evtType === 'lead_capture') return { label: 'Lead Capture', className: `${s.background} ${s.text}` };
  return undefined;
}
